// Review page helpers: issue labels, table columns, date/time formatting,
// photo links, row colouring, cache busting. Shared by Review.jsx and
// the files in ./review/. Moved out of Review.jsx 2026-10-14.
import { bustPrefix } from '../../dataCache.js';

// All tip mutations should clear the SWR cache for the read paths they
// affect — without this, the user sees up to 30s of stale data after
// their own approve/correct/unapprove click.
export function bustTipCaches() {
  bustPrefix('/api/google/tips');
  bustPrefix('/api/tips/list');
  bustPrefix('/api/balances/recon');
  bustPrefix('/api/cycle-reconcile');
}


export const ISSUE_LABEL = {
  // Errors (red — block billing until corrected)
  unknown_plate: 'Unknown plate', missing_plate: 'Missing plate',
  missing_customer: 'Missing customer',
  plate_customer_mismatch: 'Plate ≠ customer',
  // Warnings (yellow — operator should review per macro's instructions)
  tank_size_mismatch: 'Tank mismatch',
  capital_utility_split: 'Capital/Utility — confirm split (Pino)',
  aj_vax_metricon:      'A&J Vax — verify Metrocon billing',
  flat_rate_force_size_10: 'Flat-rate cust — force size 10',
  badger_location_unclear: 'Badger — which location?',
  drill_mud_check:      'Additives = Yes — confirm drill mud',
  metrocon_billing_override: '⚠ Bill to Metrocon (operator listed Metrocon)',
  missing_tank_size: 'No tank size',
  suspect_fill_pct: 'Bad fill %',
  bad_pct_value: 'Garbled fill %',
  bad_tank_size_value: 'Garbled tank size'
};
export const CRITICAL = new Set(['unknown_plate','missing_plate','missing_customer','metrocon_billing_override']);

// Column order optimized for the Pending Review workflow (per Ecovac
// Instructions doc Section 3 Step 5). Reviewer's eye lands on Status
// first to see what's flagged, then immediately the plate (clickable
// for photo) and customer for the macro's main check, then the
// match indicator, then the operational data needed for billing
// (date/time/tank/fill/additives), then secondary identifiers
// (site/contractor/truck/driver/solids), then auxiliary form fields.
export const COLS = [
  { key: '_issues',       label: 'Status' },                          // what's wrong
  { key: 'licensePlate',  label: 'Plate' },                           // click for photo / hover preview
  { key: 'customer',      label: 'Customer' },                        // paired check with plate
  { key: 'hydrovacCustomer', label: 'Hydrovac customer' },             // end-customer col (e.g. Metricon when subbed)
  { key: 'notes',         label: 'Notes (F+G)' },                     // sheet col F = 'IF OTHER...' override + col G = Contractor
  { key: '_excelMatchId', label: 'Excel match', pendingOrUnmatched: true },
  { key: 'date',          label: 'Date' },
  { key: 'arrivalTime',   label: 'Time' },
  { key: 'tankSize',      label: 'Tank',    num: true },              // billing critical
  { key: 'pctFilled',     label: 'Fill %',  num: true },              // billing critical
  { key: 'additives',     label: 'Additives',    raw: 'Additives Used:' }, // drill-mud flag
  { key: 'project_id',    label: 'Project' },                          // schema-046 rate-override project
  { key: 'site',          label: 'Site' },
  { key: 'contractor',    label: 'Contractor' },
  { key: 'truckNumber',   label: 'Truck #' },
  { key: 'driver',        label: 'Driver' },
  { key: 'pctSolids',     label: 'Solids %', num: true },
  { key: 'platePhoto',    label: 'Photo' },
  { key: 'afterHours',    label: 'After Hrs',    raw: 'After Hours (1=No, 2=Yes)' },
  { key: 'waterSource',   label: 'Water source', raw: 'On-Board Water Source (Municipality)' },
  { key: 'classification', label: 'Class.',      raw: 'What is the classification? (Check all that apply)' },
  { key: 'workType',      label: 'Work type',    raw: 'Type of work (check all that apply):' },
  { key: 'highRisk',      label: 'High risk',    raw: 'High risk locations nearby? (within 50 meters)' },
  { key: '_source',       label: 'Source' },
  { key: '_decidedAt',    label: 'Decided' }
];

// Coerce whatever date shape came off the sheet into ISO 'YYYY-MM-DD'.
// Accepts 'YYYY-MM-DD' (already ISO) or 'M/D/YYYY' (Google form default).
// Returns the input unchanged if it doesn't match either — safest
// fallback so unknown formats stay visible instead of vanishing.
export function fmtDate(d) {
  if (!d) return '';
  const s = String(d).trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return m[1] + '-' + m[2] + '-' + m[3];
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return m[3] + '-' + m[1].padStart(2,'0') + '-' + m[2].padStart(2,'0');
  return s;
}
// Coerce arrival time to 24-hour 'HH:MM:SS' so sorting works. Handles
// AM/PM suffix + optional seconds. Falls back to raw input on regex miss.
export function fmtTime(t) {
  if (!t) return '';
  const m = String(t).match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?/i);
  if (!m) return String(t).trim();
  let h = parseInt(m[1], 10);
  const ampm = (m[4] || '').toUpperCase();
  if (ampm === 'PM' && h < 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  return String(h).padStart(2,'0') + ':' + m[2] + ':' + (m[3] || '00');
}
// Fallback for when the form column header drifted and r.platePhoto
// ended up null at import time. Scans _raw for any value that looks
// like a Google Drive link.
export function findPlatePhoto(r) {
  if (r.platePhoto) return r.platePhoto;
  const raw = r._raw || {};
  for (const v of Object.values(raw)) {
    if (typeof v === 'string' && /drive\.google\.com/.test(v)) return v;
  }
  return null;
}
// Convert a Drive URL to our backend proxy (used for the <img> hover
// preview). The proxy requires a one-time Drive API + folder-sharing
// setup; until that's done, this returns a URL that will 404.
export function toDriveProxy(url, tipId) {
  if (!url) return null;
  const m = String(url).match(/[?&]id=([\w-]+)/) || String(url).match(/\/file\/d\/([\w-]+)/);
  if (!m) return null;
  // tipId lets the backend lazily ARCHIVE the photo on view, and serve
  // the archived copy if the Drive file has been deleted (2026-10-06).
  return '/api/drive/image?id=' + m[1] + (tipId ? '&tip=' + encodeURIComponent(tipId) : '');
}
// Convert a Drive URL to the native /file/d/<id>/preview embed URL.
// This is what the modal uses — works in any user's browser as long as
// the Drive file is shared 'anyone with link can view' (the default
// for Google Forms file uploads). No backend / service-account setup
// required.
export function toDriveEmbed(url) {
  if (!url) return null;
  const m = String(url).match(/[?&]id=([\w-]+)/) || String(url).match(/\/file\/d\/([\w-]+)/);
  return m ? 'https://drive.google.com/file/d/' + m[1] + '/preview' : null;
}
// Row background color for the Review table:
//   no issues        -> pale green  (all clear)
//   any CRITICAL     -> pale red    (blocks approval)
//   only warnings    -> pale yellow (approve with confirmation)
export function rowTint(issues) {
  if (!issues || issues.length === 0) return { background: '#f0fdf4' };
  if (issues.some(i => CRITICAL.has(i))) return { background: '#fef2f2' };
  return { background: '#fefce8' };
}
// Status pill shown in the Status column — text + color, matching
// the rowTint 3-tier scheme.
export function statusLabel(issues) {
  if (!issues || issues.length === 0) return { label: 'Clean', color: '#16a34a' };
  if (issues.some(i => CRITICAL.has(i))) return { label: 'Critical', color: '#dc2626' };
  return { label: 'Warning', color: '#d97706' };
}
// Minimal JSON fetcher used by the few Review-scoped diagnostics that
// haven't been migrated to useApi(). Throws on non-2xx with the body
// so caller can surface a real error message.
export async function getJson(url) {
  const r = await fetch(url, { credentials: 'include' });
  if (!r.ok) throw new Error(r.status + ' ' + (await r.text()));
  return r.json();
}
